// MUST be first — the polyfill module shims `globalThis.process` before any
// other module evaluates. ESM hoists `import` declarations above any
// top-level code, so an inline polyfill block here wouldn't run early enough.
import "@/app/process-polyfill";

import "@/app/styles/globals.css";
import "@/app/styles/component-overrides.css";
import "@/app/styles/tldraw.css";
import "@/app/styles/sidebar.css";
import "@/app/styles/portal.css";
import "@/app/styles/themed-scrollbar.css";
import "@/app/styles/smooth-scroll.css";
import "@/app/styles/chat.css";
import "@/app/styles/phone-selector.css";
import "@/app/styles/fonts.css";
import "@/shared/lib/events/index";

import { RouterProvider } from "@tanstack/react-router";
import { enableHistorySync } from "nuqs/adapters/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { z } from "zod";
import { router } from "@/app/router/index";
import { bootstrapApiClient } from "@/shared/api/client-bootstrap";
import { initSentry, wireRouterTracing } from "@/shared/lib/sentry";
import { installZodLocale } from "@/shared/lib/zod-locale";

initSentry();
wireRouterTracing(router);
bootstrapApiClient();
// Zod's own messages ("Invalid URL", "Required", …) are English constants until
// this global error map is installed. Must run before the first parse.
installZodLocale();

// nuqs uses the generic `nuqs/adapters/react` adapter, which by default only
// reacts to `popstate` and to nuqs's OWN URL writes. TanStack Router drives
// navigation through `history.pushState`, so a programmatic `navigate({ to,
// search })` (e.g. `useOpenDocument` opening a contact from the calendar
// drawer while already on /documents) was invisible to nuqs — `useQueryStates`
// never saw the new `phoneE164` and the document never loaded. `enableHistorySync`
// patches the History API so nuqs observes those third-party navigations too.
//
// MUST run AFTER `createRouter` (triggered by the `@/app/router` import above) so it
// wraps TanStack's patched `history.pushState` rather than being wrapped by it.
enableHistorySync();

// A tab that outlived a deploy asks for chunks the server no longer has; reload
// it into the current build, once per build so a missing chunk cannot loop.
if (import.meta.env.PROD) {
	const CHUNK_RELOAD_KEY = "__chunk_reload_for_build__";
	window.addEventListener("vite:preloadError", () => {
		if (
			sessionStorage.getItem(CHUNK_RELOAD_KEY) ===
			import.meta.env.VITE_APP_BUILD_ID
		) {
			return;
		}
		sessionStorage.setItem(CHUNK_RELOAD_KEY, import.meta.env.VITE_APP_BUILD_ID);
		window.location.reload();
	});
}

const DeployedBuildSchema = z.object({ buildId: z.string() });

// Offline app shell: register the unified service worker (offline shell + web
// push, built from src/sw.ts) in PRODUCTION builds. Dev normally keeps the
// push-only worker at public/sw.js (registered on demand by web-push-client) —
// no dev-time fetch interception, so HMR and fresh assets are never cached.
//
// Opt-in: `VITE_PWA_DEV=1 bun dev` registers a hand-rolled CLASSIC dev worker
// (public/dev-offline-sw.js) so offline can be exercised without a build. It is
// deliberately NOT vite-plugin-pwa's dev SW (an ES-module + workbox worker that
// fails to start in dev). The prod build serves the real shell at `/sw.js`.
const pwaDevEnabled = import.meta.env.VITE_PWA_DEV === "1";
const pwaDisabled = import.meta.env.VITE_DISABLE_PWA === "1";
if (
	!pwaDisabled &&
	(import.meta.env.PROD || pwaDevEnabled) &&
	"serviceWorker" in navigator
) {
	const swUrl = import.meta.env.PROD ? "/sw.js" : "/dev-offline-sw.js";
	window.addEventListener("load", () => {
		navigator.serviceWorker
			.register(swUrl, { scope: "/" })
			.then((registration) => {
				if (!(import.meta.env.PROD && registration)) {
					return;
				}

				navigator.serviceWorker.ready.then((ready) => {
					ready.active?.postMessage({ type: "backfill-assets" });
				});
				// A worker that takes over mid-visit fills in its own build's assets.
				navigator.serviceWorker.addEventListener("controllerchange", () => {
					navigator.serviceWorker.controller?.postMessage({
						type: "backfill-assets",
					});
				});

				// A deploy is detected by its build id, not through the worker:
				// pages load network-first, so one reload always picks it up.
				// Checked on load, hourly, and when the tab comes back (throttled);
				// the hourly check only reloads a hidden tab, never one in use.
				// One reload per deployed build per tab, so it cannot loop.
				const HOUR_MS = 60 * 60 * 1000;
				const VISIBILITY_THROTTLE_MS = 5 * 60 * 1000;
				const RELOADED_FOR_KEY = "__reloaded_for_build__";
				let lastCheck = 0;
				const reloadIfStale = async (interruptVisibleTab: boolean) => {
					lastCheck = Date.now();
					registration.update().catch(() => undefined);
					const response = await fetch("/version.json", { cache: "no-store" });
					const deployed = DeployedBuildSchema.safeParse(
						response.ok ? await response.json() : null,
					);
					if (
						!deployed.success ||
						deployed.data.buildId === import.meta.env.VITE_APP_BUILD_ID ||
						sessionStorage.getItem(RELOADED_FOR_KEY) ===
							deployed.data.buildId ||
						(!interruptVisibleTab && document.visibilityState === "visible")
					) {
						return;
					}
					sessionStorage.setItem(RELOADED_FOR_KEY, deployed.data.buildId);
					window.location.reload();
				};

				reloadIfStale(true).catch(() => undefined);
				setInterval(() => {
					reloadIfStale(false).catch(() => undefined);
				}, HOUR_MS);
				document.addEventListener("visibilitychange", () => {
					if (
						document.visibilityState === "visible" &&
						Date.now() - lastCheck >= VISIBILITY_THROTTLE_MS
					) {
						reloadIfStale(true).catch(() => undefined);
					}
				});
			})
			.catch((error) => {
				console.error("[main] Service worker registration failed", error);
			});

		// DEV ONLY: the dev worker runtime-caches on demand, so the FIRST visit
		// (SW activates after the shell + module graph already loaded) leaves the
		// cache empty and a later offline reload would fail. When the dev SW first
		// takes control, reload once so the whole graph is re-fetched THROUGH it
		// and cached. Guarded per-tab so it can't loop. Prod precaches at install,
		// so it never needs this.
		if (pwaDevEnabled && !import.meta.env.PROD) {
			const RELOADED_KEY = "__dev_sw_warm_reload__";
			navigator.serviceWorker.addEventListener("controllerchange", () => {
				if (sessionStorage.getItem(RELOADED_KEY)) {
					return;
				}
				sessionStorage.setItem(RELOADED_KEY, "1");
				window.location.reload();
			});
		}
	});
} else if (
	(!import.meta.env.PROD || pwaDisabled) &&
	"serviceWorker" in navigator
) {
	// Plain `bun dev` (flag off): actively UNREGISTER any lingering dev offline
	// worker from a previous `VITE_PWA_DEV=1` session. SW registrations persist
	// across dev sessions; a stale one keeps intercepting module requests and,
	// on a cache miss while Vite is restarting/re-optimizing, answers
	// `Response.error()` → `net::ERR_FAILED` on lazy chunks ("Failed to load
	// documents") and hung loads. The push-only worker (public/sw.js) is
	// registered on demand by web-push-client and must NOT be touched.
	//
	// Runs IMMEDIATELY at module eval, NOT on `window` "load": a stale worker
	// that wedges intercepts the very requests the page needs to finish
	// loading, so `load` never fires and a load-gated cleanup can never run —
	// a chicken-and-egg that left a dev-offline-sw registration pinning
	// localhost:3000 across sessions (found registered on-disk as REG 994
	// while every localhost:3000 navigation hung with the Vite server idle).
	navigator.serviceWorker
		.getRegistrations()
		.then((registrations) => {
			for (const registration of registrations) {
				const scriptUrl =
					registration.active?.scriptURL ??
					registration.waiting?.scriptURL ??
					registration.installing?.scriptURL ??
					"";
				if (
					pwaDisabled ||
					scriptUrl.includes("dev-offline-sw.js") ||
					scriptUrl.includes("dev-sw.js")
				) {
					registration.unregister();
				}
			}
		})
		.catch(() => {
			// Best-effort cleanup — never block boot on it.
		});
}

const rootEl = document.getElementById("root");
if (!rootEl) {
	throw new Error("Root element #root not found in index.html");
}

// AppProviders is mounted INSIDE the router (in router-components.tsx's RootLayout) so
// providers that need router hooks have a RouterProvider parent.
createRoot(rootEl).render(
	<StrictMode>
		<RouterProvider router={router} />
	</StrictMode>,
);
